import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ShoppingBag, Camera, Shirt, Bell, MessageCircle, Vote, Link2, ImageIcon, ScanBarcode, Sparkles, TrendingDown, FolderHeart } from 'lucide-react';
import confetti from 'canvas-confetti';
import WantWordmark from '@/assets/want-w-icon.svg';

// Platform logos for the embedded demo
import instagramLogo from '@/assets/instagram-logo.png';
import tiktokLogo from '@/assets/tiktok-logo.png';
import zaraLogo from '@/assets/zara-logo.svg';
import asosLogo from '@/assets/asos-logo.svg';

// Real product imagery for embeds
import demoPerson from '@/assets/demo-person.jpg';
import demoDress from '@/assets/demo-dress.jpg';
import demoTryonResult from '@/assets/demo-tryon-result.jpg';
import demoProduct1 from '@/assets/demo-product-1.jpg';
import demoProduct2 from '@/assets/demo-product-2.jpg';
import demoProduct3 from '@/assets/demo-product-3.jpg';
import demoProduct4 from '@/assets/demo-product-4.jpg';

// Demo avatars for poll embed
import demoAvatar1 from '@/assets/demo-avatar-1.jpg';
import demoAvatar2 from '@/assets/demo-avatar-2.jpg';
import demoAvatar3 from '@/assets/demo-avatar-3.jpg';

// ─── Embed Components ───────────────────────────────────────────

function PlatformLogosEmbed() {
  const platforms = [
    { logo: instagramLogo, name: 'Instagram' },
    { logo: tiktokLogo, name: 'TikTok' },
    { logo: zaraLogo, name: 'Zara' },
    { logo: asosLogo, name: 'ASOS' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex items-center gap-3 px-3 py-2.5 bg-background/80 rounded-xl border border-border/50"
    >
      {platforms.map((p, i) => (
        <motion.div
          key={p.name}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.12 }}
          className="flex flex-col items-center gap-1"
        >
          <div className="w-10 h-10 rounded-xl bg-white dark:bg-zinc-800 border border-border/40 flex items-center justify-center p-1.5 shadow-sm">
            <img src={p.logo} alt={p.name} className="w-full h-full object-contain" />
          </div>
          <span className="text-[8px] text-muted-foreground font-medium">{p.name}</span>
        </motion.div>
      ))}
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-[9px] text-muted-foreground ml-1"
      >
        +100s more
      </motion.span>
    </motion.div>
  );
}

function UrlInputEmbed() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex items-center gap-2 px-3 py-2.5 bg-background/80 rounded-xl border border-border/50 text-xs"
    >
      <Link2 className="w-3.5 h-3.5 text-muted-foreground" />
      <span className="text-muted-foreground font-mono truncate flex-1">https://zara.com/dress...</span>
      <span className="px-2.5 py-1 bg-primary text-primary-foreground rounded-full text-[10px] font-semibold">Paste</span>
    </motion.div>
  );
}

function QuickActionsEmbed() {
  const actions = [
    { icon: Camera, label: 'Camera' },
    { icon: ImageIcon, label: 'Gallery' },
    { icon: ScanBarcode, label: 'Scan' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex gap-2"
    >
      {actions.map((action, i) => (
        <motion.div
          key={action.label}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: i * 0.1 }}
          className="flex flex-col items-center gap-1 px-4 py-2.5 bg-background/80 rounded-xl border border-border/50"
        >
          <action.icon className="w-5 h-5 text-foreground" />
          <span className="text-[9px] text-muted-foreground font-medium">{action.label}</span>
        </motion.div>
      ))}
    </motion.div>
  );
}

function CollectionsEmbed() {
  const collections = [
    { name: 'Summer ☀️', count: 12, images: [demoProduct1, demoProduct2] },
    { name: 'Work fits', count: 8, images: [demoProduct3, demoProduct4] },
    { name: 'Wishlist 💕', count: 23, images: [demoDress, demoProduct1] },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex gap-2"
    >
      {collections.map((col, i) => (
        <motion.div
          key={col.name}
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: i * 0.1 }}
          className="flex flex-col gap-1"
        >
          <div className="w-[72px] h-[72px] rounded-xl overflow-hidden bg-muted border border-border/40 relative grid grid-cols-2 gap-px">
            {col.images.map((img, j) => (
              <img key={j} src={img} alt="" className="w-full h-full object-cover" />
            ))}
          </div>
          <span className="text-[9px] font-medium text-foreground truncate w-[72px]">{col.name}</span>
          <span className="text-[8px] text-muted-foreground">{col.count} items</span>
        </motion.div>
      ))}
    </motion.div>
  );
}

function TryOnPreviewEmbed() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex items-center gap-2.5 p-2.5 bg-background/80 rounded-xl border border-border/50"
    >
      <div className="w-16 h-20 rounded-lg overflow-hidden bg-muted">
        <img src={demoPerson} alt="" className="w-full h-full object-cover" />
      </div>
      <span className="text-muted-foreground text-lg">+</span>
      <div className="w-16 h-20 rounded-lg overflow-hidden bg-muted">
        <img src={demoDress} alt="" className="w-full h-full object-cover" />
      </div>
      <span className="text-muted-foreground text-lg">→</span>
      <motion.div
        animate={{ boxShadow: ['0 0 0 0px hsl(var(--primary) / 0)', '0 0 0 4px hsl(var(--primary) / 0.3)', '0 0 0 0px hsl(var(--primary) / 0)'] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="w-16 h-20 rounded-lg overflow-hidden border-2 border-primary/40 relative"
      >
        <img src={demoTryonResult} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/10 to-transparent" />
        <Sparkles className="w-3.5 h-3.5 text-primary absolute top-1 right-1 drop-shadow-sm" />
      </motion.div>
    </motion.div>
  );
}

function ProductResultsEmbed() {
  const products = [
    { price: '£35', store: 'Zara', image: demoProduct1 },
    { price: '£42', store: 'ASOS', image: demoProduct2 },
    { price: '£48', store: 'H&M', image: demoProduct3 },
    { price: '£29', store: 'Shein', image: demoProduct4 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex gap-2"
    >
      {products.map((product, i) => (
        <motion.div
          key={i}
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: i * 0.12 }}
          className="w-20 h-24 rounded-xl overflow-hidden bg-muted border border-border/50 shadow-sm relative flex-shrink-0"
        >
          <img src={product.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1">
            <span className="text-[9px] text-white font-bold block">{product.price}</span>
            <span className="text-[7px] text-white/70">{product.store}</span>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}

function PriceDropEmbed() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex items-center gap-3 p-2.5 bg-background/80 rounded-xl border border-border/50"
    >
      <div className="w-14 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
        <img src={demoProduct2} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <motion.div
            animate={{ rotate: [0, -15, 15, 0] }}
            transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 2 }}
          >
            <Bell className="w-3.5 h-3.5 text-green-500" />
          </motion.div>
          <span className="text-[10px] font-bold text-green-500">Price dropped!</span>
        </div>
        <p className="text-[10px] text-muted-foreground truncate">ASOS Midi Dress</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-[10px] text-muted-foreground line-through">£65</span>
          <TrendingDown className="w-3 h-3 text-green-500" />
          <span className="text-xs font-bold text-green-600 dark:text-green-400">£42</span>
        </div>
      </div>
    </motion.div>
  );
}

function PollEmbed() {
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimate(true), 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-2.5 bg-background/80 rounded-xl border border-border/50 space-y-2"
    >
      <div className="flex items-center gap-2">
        <div className="flex -space-x-2">
          {[demoAvatar1, demoAvatar2, demoAvatar3].map((avatar, i) => (
            <motion.div
              key={i}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: i * 0.1, type: 'spring' }}
              className="w-6 h-6 rounded-full border-2 border-white dark:border-zinc-800 overflow-hidden"
            >
              <img src={avatar} alt="" className="w-full h-full object-cover" />
            </motion.div>
          ))}
        </div>
        <span className="text-[10px] text-muted-foreground">3 friends voted</span>
      </div>
      <div className="flex gap-2">
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-1">
            <span className="text-[10px] font-medium">Option A</span>
            <span className="text-[9px] text-muted-foreground">🔥</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: animate ? '75%' : 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="h-full bg-primary rounded-full"
            />
          </div>
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-1">
            <span className="text-[10px] font-medium">Option B</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: animate ? '25%' : 0 }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
              className="h-full bg-muted-foreground/30 rounded-full"
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Typing Indicator ───────────────────────────────────────────

function TypingIndicator() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      className="flex items-center gap-1 px-4 py-3 bg-[#E9E9EB] dark:bg-zinc-700 rounded-2xl rounded-bl-md w-fit"
    >
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="w-2 h-2 bg-zinc-400 dark:bg-zinc-500 rounded-full"
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
        />
      ))}
    </motion.div>
  );
}

// ─── Delivered / Read timestamps ────────────────────────────────

function DeliveredText({ delay }: { delay: number }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay }}
      className="flex justify-end pr-2"
    >
      <span className="text-[9px] text-muted-foreground/60 font-medium">Delivered</span>
    </motion.div>
  );
}

// ─── Heart Reaction Overlay ─────────────────────────────────────

function HeartReaction({ delay }: { delay: number }) {
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay, type: 'spring', damping: 10, stiffness: 200 }}
      className="absolute -bottom-2 -right-1 bg-white dark:bg-zinc-800 rounded-full px-1.5 py-0.5 shadow-md border border-border/30 flex items-center gap-0.5"
    >
      <Heart className="w-3 h-3 text-red-500 fill-red-500" />
    </motion.div>
  );
}

// ─── Message Script ─────────────────────────────────────────────

interface MessageDef {
  id: number;
  text?: string;
  delay: number;
  isReply?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  embedType?: string;
  showDelivered?: boolean;
  showHeartReaction?: boolean;
}

const MESSAGES: MessageDef[] = [
  { id: 1, text: "wait have you tried WNTD yet?? 👀", delay: 0.6 },
  { id: 2, text: "no what is it", delay: 2.0, isReply: true },
  { id: 3, text: "ok so basically you can save ANY outfit you see online", delay: 3.5, icon: Heart },
  { id: 4, delay: 4.5, embedType: 'platform-logos' },
  { id: 5, text: "just paste a link or snap a photo:", delay: 6.5 },
  { id: 6, delay: 7.5, embedType: 'url-input' },
  { id: 7, delay: 8.5, embedType: 'quick-actions' },
  { id: 8, text: "wait that's actually so easy", delay: 10.5, isReply: true },
  { id: 9, text: "and you organize everything into collections 📁", delay: 12.5, icon: FolderHeart },
  { id: 10, delay: 13.5, embedType: 'collections' },
  { id: 11, text: "and then you can try stuff on virtually before buying 👗", delay: 16.0, icon: Shirt },
  { id: 12, delay: 17.0, embedType: 'tryon-preview', showHeartReaction: true },
  { id: 13, text: "ok that's kinda cool ngl 😮", delay: 19.5, isReply: true, showDelivered: true },
  { id: 14, text: "there's also an AI that helps you find things", delay: 21.5, icon: MessageCircle },
  { id: 15, text: "like 'find me a black midi dress under £50'", delay: 23.0 },
  { id: 16, delay: 24.0, embedType: 'product-results' },
  { id: 17, text: "the AI literally found me the exact same top for half the price 😭", delay: 27.0 },
  { id: 18, text: "plus you get alerts when prices drop!!", delay: 29.0, icon: Bell },
  { id: 19, delay: 30.0, embedType: 'price-drop' },
  { id: 20, text: "and you can get friends to vote on outfits", delay: 32.5, icon: Vote },
  { id: 21, delay: 33.5, embedType: 'poll' },
  { id: 22, text: "omg downloading rn 😍", delay: 36.0, isReply: true },
];

function renderEmbed(type: string) {
  switch (type) {
    case 'platform-logos': return <PlatformLogosEmbed />;
    case 'url-input': return <UrlInputEmbed />;
    case 'quick-actions': return <QuickActionsEmbed />;
    case 'collections': return <CollectionsEmbed />;
    case 'tryon-preview': return <TryOnPreviewEmbed />;
    case 'product-results': return <ProductResultsEmbed />;
    case 'price-drop': return <PriceDropEmbed />;
    case 'poll': return <PollEmbed />;
    default: return null;
  }
}

// ─── Main Component ─────────────────────────────────────────────

interface ChatDemoScreenProps {
  onComplete?: () => void;
}

export function ChatDemoScreen({ onComplete }: ChatDemoScreenProps) {
  const [visibleIds, setVisibleIds] = useState<Set<number>>(new Set());
  const [typingFor, setTypingFor] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const confettiFired = useRef(false);

  // Progressive reveal with typing indicators for replies
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    MESSAGES.forEach((msg) => {
      // Show typing indicator 1s before reply messages
      if (msg.isReply) {
        timers.push(setTimeout(() => setTypingFor(msg.id), msg.delay * 1000 - 1200));
      }

      timers.push(setTimeout(() => {
        setTypingFor((current) => (current === msg.id ? null : current));
        setVisibleIds((prev) => new Set(prev).add(msg.id));
      }, msg.delay * 1000));
    });

    return () => timers.forEach(clearTimeout);
  }, []);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [visibleIds, typingFor]);

  // Confetti on final message
  useEffect(() => {
    if (visibleIds.has(22) && !confettiFired.current) {
      confettiFired.current = true;
      confetti({ particleCount: 80, spread: 60, origin: { y: 0.7 }, colors: ['#FF5C4D', '#FF6F3B', '#FFD700', '#FF69B4'] });
    }
  }, [visibleIds]);

  // Complete callback
  useEffect(() => {
    const t = setTimeout(() => onComplete?.(), 40000);
    return () => clearTimeout(t);
  }, [onComplete]);

  const springConfig = { type: 'spring' as const, damping: 18, stiffness: 180, mass: 0.8 };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden bg-white dark:bg-zinc-900">
      {/* iMessage header */}
      <div className="flex items-center justify-center gap-2 py-3 border-b border-border/30 bg-gray-50/80 dark:bg-zinc-800/80 backdrop-blur-xl shrink-0">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-sm font-semibold">
          S
        </div>
        <span className="font-semibold text-foreground">bestie 💕</span>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-4 pb-12 space-y-2.5">
        <AnimatePresence>
          {MESSAGES.filter((m) => visibleIds.has(m.id)).map((message) => {
            const Icon = message.icon;
            const isReply = message.isReply;
            const isEmbed = !!message.embedType;

            return (
              <div key={message.id}>
                <motion.div
                  initial={{ opacity: 0, x: isReply ? 60 : -60, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  transition={springConfig}
                  className={`flex ${isReply ? 'justify-end' : 'justify-start'}`}
                >
                  {isEmbed ? (
                    <div className="max-w-[90%] px-3 py-2 rounded-2xl rounded-bl-md bg-[#E9E9EB] dark:bg-zinc-700 relative">
                      {renderEmbed(message.embedType!)}
                      {message.showHeartReaction && <HeartReaction delay={1.5} />}
                    </div>
                  ) : (
                    <div
                      className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                        isReply
                          ? 'bg-primary text-primary-foreground rounded-br-md'
                          : 'bg-[#E9E9EB] dark:bg-zinc-700 text-foreground rounded-bl-md'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {Icon && !isReply && (
                          <Icon className="w-4 h-4 text-primary shrink-0" />
                        )}
                        <span className="text-[15px] leading-relaxed">{message.text}</span>
                      </div>
                    </div>
                  )}
                </motion.div>
                {message.showDelivered && <DeliveredText delay={0.5} />}
              </div>
            );
          })}

          {/* Typing indicator */}
          {typingFor && (
            <motion.div
              key={`typing-${typingFor}`}
              className="flex justify-end"
            >
              <TypingIndicator />
            </motion.div>
          )}

          {/* App preview card */}
          {visibleIds.has(22) && (
            <motion.div
              initial={{ opacity: 0, x: -60, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ ...springConfig, delay: 1.5 }}
              className="flex justify-start"
            >
              <div className="max-w-[80%] rounded-2xl rounded-bl-md overflow-hidden bg-[#E9E9EB] dark:bg-zinc-700">
                <div className="flex items-center gap-3 p-3 border-b border-black/5 dark:border-white/5">
                  <div className="w-10 h-10 rounded-xl bg-foreground flex items-center justify-center p-1.5">
                    <img src={WantWordmark} alt="WNTD" className="h-full w-auto brightness-0 invert" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">WNTD</p>
                    <p className="text-xs text-muted-foreground">Save, Try, Shop Smarter</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
